import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  AlertTriangle,
  TrendingUp,
  Percent,
  Calendar,
  Info,
  SlidersHorizontal,
  ChevronRight,
  UserX,
} from 'lucide-react';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';
import { corFrequencia } from './relatorios-professor-shared';
import type { FrequenciaAluno } from '../../api/hooks/useRelatorios';
import type { RelatoriosProfessorPageContext } from './useRelatoriosProfessorPage';

interface Props {
  ctx: RelatoriosProfessorPageContext;
}

export function RelatoriosMobileView({ ctx }: Props) {
  const navigate = useNavigate();
  const {
    minhasTurmas,
    selectedTurmaId,
    setSelectedTurmaId,
    filtrosAbertos,
    setFiltrosAbertos,
    somenteRisco,
    setSomenteRisco,
    freq,
    freqQuery,
    cons,
    alunosExibidos,
  } = ctx;

  const renderAlunoRow = (aluno: FrequenciaAluno) => {
    const cor = corFrequencia(aluno.percentual);

    return (
      <button
        key={aluno.alunoId}
        type="button"
        className="rel-item--tap"
        onClick={() => navigate(`/professor/alunos/${aluno.alunoId}`)}
      >
        <div
          className="rel-item-icon"
          style={{
            background: aluno.emRisco ? 'rgba(220,38,38,0.1)' : 'rgba(37,99,235,0.1)',
            color: aluno.emRisco ? '#dc2626' : '#2563eb',
          }}
        >
          {aluno.emRisco ? <AlertTriangle size={14} /> : <Users size={14} />}
        </div>
        <div className="rel-item-body">
          <div className="rel-item-title-row">
            <span className="rel-item-title">{aluno.nome}</span>
            {aluno.emRisco && (
              <span
                className="rel-item-badge"
                style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}
              >
                Risco
              </span>
            )}
          </div>
          <p className="rel-item-meta">
            {aluno.presentes}/{aluno.totalDias} dias
            {aluno.porBimestre.length > 0
              ? ` · pior bim ${Math.min(...aluno.porBimestre.map((b) => b.percentual))}%`
              : ''}
          </p>
        </div>
        <div className="rel-item-aside">
          <span className="rel-item-pct" style={{ color: cor }}>
            {aluno.percentual}%
          </span>
          <ChevronRight size={16} className="rel-item-chevron" aria-hidden />
        </div>
      </button>
    );
  };

  return (
    <MobilePageWrapper title="Relatórios" icon={BarChart3} iconColor="#93c5fd">
      <div className="admin-page rel-page-root rel-page-mobile">
        <div className="rel-hint-banner">
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
          <span>
            <strong>Seu painel de frequência</strong> — só suas turmas. Priorize alunos em risco
            (&lt;75%), compare bimestres antes de reuniões com famílias e abra a ficha em um toque.
          </span>
        </div>

        {cons && (
          <div className="rel-kpi-row-compact">
            <div className="rel-kpi-chip">
              <Users size={14} style={{ color: '#2563eb' }} aria-hidden />
              <span className="rel-kpi-chip-value">{cons.resumo.totalAlunos}</span>
              <span className="rel-kpi-chip-label">Meus alunos</span>
            </div>
            <div className="rel-kpi-chip">
              <Percent size={14} style={{ color: '#059669' }} aria-hidden />
              <span className="rel-kpi-chip-value">{cons.resumo.mediaFrequencia}%</span>
              <span className="rel-kpi-chip-label">Frequência</span>
            </div>
            <div className="rel-kpi-chip">
              <UserX size={14} style={{ color: '#dc2626' }} aria-hidden />
              <span className="rel-kpi-chip-value">{cons.resumo.alunosEmRisco ?? 0}</span>
              <span className="rel-kpi-chip-label">Em risco</span>
            </div>
            <div className="rel-kpi-chip">
              <TrendingUp size={14} style={{ color: '#7c3aed' }} aria-hidden />
              <span className="rel-kpi-chip-value">{cons.resumo.totalAvaliacoes}</span>
              <span className="rel-kpi-chip-label">Minhas av.</span>
            </div>
          </div>
        )}

        <div className="rel-toolbar">
          <div className="rel-turma-select-wrap">
            <label className="rel-filtro-label" htmlFor="rel-turma-m">
              Turma
            </label>
            <select
              id="rel-turma-m"
              className="rel-filtro-select"
              value={selectedTurmaId}
              onChange={(e) => setSelectedTurmaId(e.target.value)}
            >
              <option value="">Escolha a turma</option>
              {minhasTurmas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                  {t.serie ? ` (${t.serie})` : ''}
                </option>
              ))}
            </select>
          </div>
          {selectedTurmaId && (
            <button
              type="button"
              className={`rel-filter-toggle ${filtrosAbertos || somenteRisco ? 'active' : ''}`}
              onClick={() => setFiltrosAbertos(!filtrosAbertos)}
              aria-label="Filtros"
            >
              <SlidersHorizontal size={16} />
            </button>
          )}
        </div>

        {filtrosAbertos && selectedTurmaId && (
          <div className="rel-filtros-panel">
            <label className="rel-filtro-check">
              <input
                type="checkbox"
                checked={somenteRisco}
                onChange={(e) => setSomenteRisco(e.target.checked)}
              />
              Mostrar apenas alunos em risco (&lt;75%)
            </label>
          </div>
        )}

        {!selectedTurmaId ? (
          <div className="rel-empty">
            <Calendar size={36} style={{ opacity: 0.25 }} />
            <p>Selecione uma turma para ver quem precisa de atencao</p>
          </div>
        ) : freqQuery.isLoading ? (
          <div className="rel-empty">Carregando...</div>
        ) : !freq ? (
          <div className="rel-empty">Sem dados para esta turma</div>
        ) : (
          <>
            <div className="rel-summary-row-compact">
              <div className="rel-summary-chip">
                <span className="rel-summary-value">{freq.totalAlunos}</span>
                <span className="rel-summary-label">Alunos</span>
              </div>
              <div className="rel-summary-chip">
                <span
                  className="rel-summary-value"
                  style={{ color: corFrequencia(freq.mediaFrequencia) }}
                >
                  {freq.mediaFrequencia}%
                </span>
                <span className="rel-summary-label">Media</span>
              </div>
              <div className="rel-summary-chip">
                <span
                  className="rel-summary-value"
                  style={{ color: freq.alunosEmRisco > 0 ? '#dc2626' : '#059669' }}
                >
                  {freq.alunosEmRisco}
                </span>
                <span className="rel-summary-label">Em risco</span>
              </div>
            </div>

            {alunosExibidos.length === 0 ? (
              <div className="rel-empty">
                <p>Nenhum aluno neste filtro</p>
              </div>
            ) : (
              <div className="rel-list-compact">
                {alunosExibidos.map((a) => (
                  <div key={a.alunoId} className="rel-item">
                    {renderAlunoRow(a)}
                  </div>
                ))}
              </div>
            )}

            <div className="rel-legenda">
              <span>
                <span className="rel-legenda-dot" style={{ background: '#059669' }} /> Adequado 80%+
              </span>
              <span>
                <span className="rel-legenda-dot" style={{ background: '#d97706' }} /> Atencao
              </span>
              <span>
                <span className="rel-legenda-dot" style={{ background: '#dc2626' }} /> Risco &lt;75%
              </span>
            </div>
          </>
        )}
      </div>
    </MobilePageWrapper>
  );
}
